'use strict'

Object.defineProperty(exports, '__esModule', { value: true })

/**
 * Checks whether node is of specific bpmn type.
 *
 * @param {ModdleElement} node
 * @param {String} type
 *
 * @return {Boolean}
 */
function is(node, type) {
  if (type.indexOf(':') === -1) {
    type = 'bpmn:' + type
  }

  return typeof node.$instanceOf === 'function'
    ? node.$instanceOf(type)
    : node.$type === type
}

/**
 * Checks whether node has any of the specified types.
 *
 * @param {ModdleElement} node
 * @param {Array<String>} types
 *
 * @return {Boolean}
 */
function isAny(node, types) {
  return types.some(function (type) {
    return is(node, type)
  })
}

var index_esm = /*#__PURE__*/ Object.freeze({
  __proto__: null,
  is: is,
  isAny: isAny,
})

var noUndefinedTask = function () {
  function check(node, reporter) {
    if (node.$type === 'bpmn:Task') {
      reporter.report(
        node.id,
        'This Task Type cannot be executed. Click on the Wrench icon to change the type.',
      )
    }
  }

  return {
    check: check,
  }
}

/**
 * Flatten array, one level deep.
 *
 * @param {Array<?>} arr
 *
 * @return {Array<?>}
 */
function flatten(arr) {
  return Array.prototype.concat.apply([], arr)
}

var nativeToString = Object.prototype.toString
var nativeHasOwnProperty = Object.prototype.hasOwnProperty
function isUndefined(obj) {
  return obj === undefined
}
function isDefined(obj) {
  return obj !== undefined
}
function isNil(obj) {
  return obj == null
}
function isArray(obj) {
  return nativeToString.call(obj) === '[object Array]'
}
function isObject(obj) {
  return nativeToString.call(obj) === '[object Object]'
}
function isNumber(obj) {
  return nativeToString.call(obj) === '[object Number]'
}
function isFunction(obj) {
  var tag = nativeToString.call(obj)
  return (
    tag === '[object Function]' ||
    tag === '[object AsyncFunction]' ||
    tag === '[object GeneratorFunction]' ||
    tag === '[object AsyncGeneratorFunction]' ||
    tag === '[object Proxy]'
  )
}
function isString(obj) {
  return nativeToString.call(obj) === '[object String]'
}
/**
 * Ensure collection is an array.
 *
 * @param {Object} obj
 */

function ensureArray(obj) {
  if (isArray(obj)) {
    return
  }

  throw new Error('must supply array')
}
/**
 * Return true, if target owns a property with the given key.
 *
 * @param {Object} target
 * @param {String} key
 *
 * @return {Boolean}
 */

function has(target, key) {
  return nativeHasOwnProperty.call(target, key)
}

/**
 * Find element in collection.
 *
 * @param  {Array|Object} collection
 * @param  {Function|Object} matcher
 *
 * @return {Object}
 */

function find(collection, matcher) {
  matcher = toMatcher(matcher)
  var match
  forEach(collection, function (val, key) {
    if (matcher(val, key)) {
      match = val
      return false
    }
  })
  return match
}
/**
 * Find element index in collection.
 *
 * @param  {Array|Object} collection
 * @param  {Function} matcher
 *
 * @return {Object}
 */

function findIndex(collection, matcher) {
  matcher = toMatcher(matcher)
  var idx = isArray(collection) ? -1 : undefined
  forEach(collection, function (val, key) {
    if (matcher(val, key)) {
      idx = key
      return false
    }
  })
  return idx
}
/**
 * Find element in collection.
 *
 * @param  {Array|Object} collection
 * @param  {Function} matcher
 *
 * @return {Array} result
 */

function filter(collection, matcher) {
  var result = []
  forEach(collection, function (val, key) {
    if (matcher(val, key)) {
      result.push(val)
    }
  })
  return result
}
/**
 * Iterate over collection; returning something
 * (non-undefined) will stop iteration.
 *
 * @param  {Array|Object} collection
 * @param  {Function} iterator
 *
 * @return {Object} return result that stopped the iteration
 */

function forEach(collection, iterator) {
  var val, result

  if (isUndefined(collection)) {
    return
  }

  var convertKey = isArray(collection) ? toNum : identity

  for (var key in collection) {
    if (has(collection, key)) {
      val = collection[key]
      result = iterator(val, convertKey(key))

      if (result === false) {
        return val
      }
    }
  }
}
/**
 * Return collection without element.
 *
 * @param  {Array} arr
 * @param  {Function} matcher
 *
 * @return {Array}
 */

function without(arr, matcher) {
  if (isUndefined(arr)) {
    return []
  }

  ensureArray(arr)
  matcher = toMatcher(matcher)
  return arr.filter(function (el, idx) {
    return !matcher(el, idx)
  })
}
/**
 * Reduce collection, returning a single result.
 *
 * @param  {Object|Array} collection
 * @param  {Function} iterator
 * @param  {Any} result
 *
 * @return {Any} result returned from last iterator
 */

function reduce(collection, iterator, result) {
  forEach(collection, function (value, idx) {
    result = iterator(result, value, idx)
  })
  return result
}
/**
 * Return true if every element in the collection
 * matches the criteria.
 *
 * @param  {Object|Array} collection
 * @param  {Function} matcher
 *
 * @return {Boolean}
 */

function every(collection, matcher) {
  return !!reduce(
    collection,
    function (matches, val, key) {
      return matches && matcher(val, key)
    },
    true,
  )
}
/**
 * Return true if some elements in the collection
 * match the criteria.
 *
 * @param  {Object|Array} collection
 * @param  {Function} matcher
 *
 * @return {Boolean}
 */

function some(collection, matcher) {
  return !!find(collection, matcher)
}
/**
 * Transform a collection into another collection
 * by piping each member through the given fn.
 *
 * @param  {Object|Array}   collection
 * @param  {Function} fn
 *
 * @return {Array} transformed collection
 */

function map(collection, fn) {
  var result = []
  forEach(collection, function (val, key) {
    result.push(fn(val, key))
  })
  return result
}
/**
 * Get the collections keys.
 *
 * @param  {Object|Array} collection
 *
 * @return {Array}
 */

function keys(collection) {
  return (collection && Object.keys(collection)) || []
}
/**
 * Shorthand for `keys(o).length`.
 *
 * @param  {Object|Array} collection
 *
 * @return {Number}
 */

function size(collection) {
  return keys(collection).length
}
/**
 * Get the values in the collection.
 *
 * @param  {Object|Array} collection
 *
 * @return {Array}
 */

function values(collection) {
  return map(collection, function (val) {
    return val
  })
}
/**
 * Group collection members by attribute.
 *
 * @param  {Object|Array} collection
 * @param  {Function} extractor
 *
 * @return {Object} map with { attrValue => [ a, b, c ] }
 */

function groupBy(collection, extractor) {
  var grouped =
    arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {}
  extractor = toExtractor(extractor)
  forEach(collection, function (val) {
    var discriminator = extractor(val) || '_'
    var group = grouped[discriminator]

    if (!group) {
      group = grouped[discriminator] = []
    }

    group.push(val)
  })
  return grouped
}
function uniqueBy(extractor) {
  extractor = toExtractor(extractor)
  var grouped = {}

  for (
    var _len = arguments.length,
      collections = new Array(_len > 1 ? _len - 1 : 0),
      _key = 1;
    _key < _len;
    _key++
  ) {
    collections[_key - 1] = arguments[_key]
  }

  forEach(collections, function (c) {
    return groupBy(c, extractor, grouped)
  })
  var result = map(grouped, function (val, key) {
    return val[0]
  })
  return result
}
var unionBy = uniqueBy
/**
 * Sort collection by criteria.
 *
 * @param  {Object|Array} collection
 * @param  {String|Function} extractor
 *
 * @return {Array}
 */

function sortBy(collection, extractor) {
  extractor = toExtractor(extractor)
  var sorted = []
  forEach(collection, function (value, key) {
    var disc = extractor(value, key)
    var entry = {
      d: disc,
      v: value,
    }

    for (var idx = 0; idx < sorted.length; idx++) {
      var d = sorted[idx].d

      if (disc < d) {
        sorted.splice(idx, 0, entry)
        return
      }
    } // not inserted, append (!)

    sorted.push(entry)
  })
  return map(sorted, function (e) {
    return e.v
  })
}
/**
 * Create an object pattern matcher.
 *
 * @example
 *
 * const matcher = matchPattern({ id: 1 });
 *
 * let element = find(elements, matcher);
 *
 * @param  {Object} pattern
 *
 * @return {Function} matcherFn
 */

function matchPattern(pattern) {
  return function (el) {
    return every(pattern, function (val, key) {
      return el[key] === val
    })
  }
}

function toExtractor(extractor) {
  return isFunction(extractor)
    ? extractor
    : function (e) {
        return e[extractor]
      }
}

function toMatcher(matcher) {
  return isFunction(matcher)
    ? matcher
    : function (e) {
        return e === matcher
      }
}

function identity(arg) {
  return arg
}

function toNum(arg) {
  return Number(arg)
}

/**
 * Debounce fn, calling it only once if the given time
 * elapsed between calls.
 *
 * Lodash-style the function exposes methods to `#clear`
 * and `#flush` to control internal behavior.
 *
 * @param  {Function} fn
 * @param  {Number} timeout
 *
 * @return {Function} debounced function
 */
function debounce(fn, timeout) {
  var timer
  var lastArgs
  var lastThis
  var lastNow

  function fire(force) {
    var now = Date.now()
    var scheduledDiff = force ? 0 : lastNow + timeout - now

    if (scheduledDiff > 0) {
      return schedule(scheduledDiff)
    }

    fn.apply(lastThis, lastArgs)
    clear()
  }

  function schedule(timeout) {
    timer = setTimeout(fire, timeout)
  }

  function clear() {
    if (timer) {
      clearTimeout(timer)
    }

    timer = lastNow = lastArgs = lastThis = undefined
  }

  function flush() {
    if (timer) {
      fire(true)
    }

    clear()
  }

  function callback() {
    lastNow = Date.now()

    for (
      var _len = arguments.length, args = new Array(_len), _key = 0;
      _key < _len;
      _key++
    ) {
      args[_key] = arguments[_key]
    }

    lastArgs = args
    lastThis = this // ensure an execution is scheduled

    if (!timer) {
      schedule(timeout)
    }
  }

  callback.flush = flush
  callback.cancel = clear
  return callback
}
/**
 * Throttle fn, calling at most once
 * in the given interval.
 *
 * @param  {Function} fn
 * @param  {Number} interval
 *
 * @return {Function} throttled function
 */

function throttle(fn, interval) {
  var throttling = false
  return function () {
    if (throttling) {
      return
    }

    fn.apply(void 0, arguments)
    throttling = true
    setTimeout(function () {
      throttling = false
    }, interval)
  }
}
/**
 * Bind function against target <this>.
 *
 * @param  {Function} fn
 * @param  {Object}   target
 *
 * @return {Function} bound function
 */

function bind(fn, target) {
  return fn.bind(target)
}

function _extends() {
  _extends =
    Object.assign ||
    function (target) {
      for (var i = 1; i < arguments.length; i++) {
        var source = arguments[i]

        for (var key in source) {
          if (Object.prototype.hasOwnProperty.call(source, key)) {
            target[key] = source[key]
          }
        }
      }

      return target
    }

  return _extends.apply(this, arguments)
}

/**
 * Convenience wrapper for `Object.assign`.
 *
 * @param {Object} target
 * @param {...Object} others
 *
 * @return {Object} the target
 */

function assign(target) {
  for (
    var _len = arguments.length,
      others = new Array(_len > 1 ? _len - 1 : 0),
      _key = 1;
    _key < _len;
    _key++
  ) {
    others[_key - 1] = arguments[_key]
  }

  return _extends.apply(void 0, [target].concat(others))
}
/**
 * Sets a nested property of a given object to the specified value.
 *
 * This mutates the object and returns it.
 *
 * @param {Object} target The target of the set operation.
 * @param {(string|number)[]} path The path to the nested value.
 * @param {any} value The value to set.
 */

function set(target, path, value) {
  var currentTarget = target
  forEach(path, function (key, idx) {
    if (key === '__proto__') {
      throw new Error('illegal key: __proto__')
    }

    var nextKey = path[idx + 1]
    var nextTarget = currentTarget[key]

    if (isDefined(nextKey) && isNil(nextTarget)) {
      nextTarget = currentTarget[key] = isNaN(+nextKey) ? {} : []
    }

    if (isUndefined(nextKey)) {
      if (isUndefined(value)) {
        delete currentTarget[key]
      } else {
        currentTarget[key] = value
      }
    } else {
      currentTarget = nextTarget
    }
  })
  return target
}
/**
 * Gets a nested property of a given object.
 *
 * @param {Object} target The target of the get operation.
 * @param {(string|number)[]} path The path to the nested value.
 * @param {any} [defaultValue] The value to return if no value exists.
 */

function get(target, path, defaultValue) {
  var currentTarget = target
  forEach(path, function (key) {
    // accessing nil property yields <undefined>
    if (isNil(currentTarget)) {
      currentTarget = undefined
      return false
    }

    currentTarget = currentTarget[key]
  })
  return isUndefined(currentTarget) ? defaultValue : currentTarget
}
/**
 * Pick given properties from the target object.
 *
 * @param {Object} target
 * @param {Array} properties
 *
 * @return {Object} target
 */

function pick(target, properties) {
  var result = {}
  var obj = Object(target)
  forEach(properties, function (prop) {
    if (prop in obj) {
      result[prop] = target[prop]
    }
  })
  return result
}
/**
 * Pick all target properties, excluding the given ones.
 *
 * @param {Object} target
 * @param {Array} properties
 *
 * @return {Object} target
 */

function omit(target, properties) {
  var result = {}
  var obj = Object(target)
  forEach(obj, function (prop, key) {
    if (properties.indexOf(key) === -1) {
      result[key] = prop
    }
  })
  return result
}
/**
 * Recursively merge `...sources` into given target.
 *
 * Does support merging objects; does not support merging arrays.
 *
 * @param {Object} target
 * @param {...Object} sources
 *
 * @return {Object} the target
 */

function merge(target) {
  for (
    var _len2 = arguments.length,
      sources = new Array(_len2 > 1 ? _len2 - 1 : 0),
      _key2 = 1;
    _key2 < _len2;
    _key2++
  ) {
    sources[_key2 - 1] = arguments[_key2]
  }

  if (!sources.length) {
    return target
  }

  forEach(sources, function (source) {
    // skip non-obj sources, i.e. null
    if (!source || !isObject(source)) {
      return
    }

    forEach(source, function (sourceVal, key) {
      if (key === '__proto__') {
        return
      }

      var targetVal = target[key]

      if (isObject(sourceVal)) {
        if (!isObject(targetVal)) {
          // override target[key] with object
          targetVal = {}
        }

        target[key] = merge(targetVal, sourceVal)
      } else {
        target[key] = sourceVal
      }
    })
  })
  return target
}

var index_esm$1 = /*#__PURE__*/ Object.freeze({
  __proto__: null,
  assign: assign,
  bind: bind,
  debounce: debounce,
  ensureArray: ensureArray,
  every: every,
  filter: filter,
  find: find,
  findIndex: findIndex,
  flatten: flatten,
  forEach: forEach,
  get: get,
  groupBy: groupBy,
  has: has,
  isArray: isArray,
  isDefined: isDefined,
  isFunction: isFunction,
  isNil: isNil,
  isNumber: isNumber,
  isObject: isObject,
  isString: isString,
  isUndefined: isUndefined,
  keys: keys,
  map: map,
  matchPattern: matchPattern,
  merge: merge,
  omit: omit,
  pick: pick,
  reduce: reduce,
  set: set,
  size: size,
  some: some,
  sortBy: sortBy,
  throttle: throttle,
  unionBy: unionBy,
  uniqueBy: uniqueBy,
  values: values,
  without: without,
})

const { filter: filter$1 } = index_esm$1

const { is: is$1 } = index_esm

/**
 * Retrieve extensionElements from a given element
 *
 * @param {ModdleElement} element
 * @param {string} [type] - Optional type of extensionElements to be retrieved
 *
 * @return {Array.<ModdleElement>}
 */
var extensionElementsHelper = function getExtensionElements(element, type) {
  let elements = []
  const extensionElements = element.get('extensionElements')

  if (typeof extensionElements !== 'undefined') {
    const extensionValues = extensionElements.get('values')

    if (typeof extensionValues !== 'undefined') {
      elements = filter$1(extensionValues, function (value) {
        return is$1(value, type)
      })
    }
  }

  return elements
}

const { is: is$2, isAny: isAny$1 } = index_esm

const messageElements = ['bpmn:EndEvent', 'bpmn:IntermediateThrowEvent']
const servicTaskLike = [
  'bpmn:BusinessRuleTask',
  'bpmn:SendTask',
  'bpmn:ServiceTask',
  'bpmn:ScriptTask',
  ...messageElements,
]

var noType = function () {
  function check(node, reporter) {
    if (!isAny$1(node, servicTaskLike)) {
      return
    }

    if (isAny$1(node, messageElements)) {
      // check if the element has a message definition, other wise it's not a service task like
      if (
        !node.eventDefinitions ||
        !node.eventDefinitions.some((eventDefinition) =>
          is$2(eventDefinition, 'bpmn:MessageEventDefinition'),
        )
      ) {
        return
      }
    }

    const taskDefinition =
      extensionElementsHelper(node, 'zeebe:taskDefinition')[0] ||
      extensionElementsHelper(node, 'zeebe:TaskDefinition')[0]
    if (!taskDefinition || taskDefinition.length < 1) {
      reporter.report(node.id, 'Property “type” is missing.')
    }
  }

  return {
    check: check,
  }
}

/**
 * A rule that checks that sequence flows outgoing from a
 * conditional forking gateway or activity are
 * either default flows _or_ have a condition attached
 */
var conditionalSequenceFlows = function () {
  function check(node, reporter) {
    if (node.$type !== 'bpmn:ExclusiveGateway') {
      return
    }
    if (!isConditionalForking(node)) {
      return
    }

    const outgoing = node.outgoing || []
    const incoming = node.incoming || []

    outgoing.forEach((flow) => {
      let missingCondition = !hasCondition(flow) && !isDefaultFlow(node, flow)

      if (outgoing.length == 1 && incoming.length > 1) {
        missingCondition = false
      }

      if (missingCondition) {
        reporter.report(
          flow.id,
          'Property “Condition expression” is missing. Alternative: Configure this flow as the default flow. ',
        )
      }
    })
  }

  return {
    check,
  }
}

// helpers /////////////////////////////

function isConditionalForking(node) {
  const defaultFlow = node['default']
  const outgoing = node.outgoing && node.outgoing.length > 0

  return defaultFlow || outgoing
}

function hasCondition(flow) {
  return !!flow.conditionExpression
}

function isDefaultFlow(node, flow) {
  return node['default'] === flow
}

const { is: is$3 } = index_esm

var noErrordefinition = function () {
  function check(node, reporter) {
    if (!is$3(node, 'bpmn:ErrorEventDefinition')) {
      return
    }

    if (!node.errorRef) {
      reporter.report(node.$parent.id, 'Property “Global Error“ is missing.')
    }
  }

  return {
    check: check,
  }
}

const { is: is$4 } = index_esm

var noMessagedefinition = function () {
  function check(node, reporter) {
    if (!is$4(node, 'bpmn:MessageEventDefinition')) {
      return
    }

    let parent = node.$parent
    if (
      is$4(parent, 'bpmn:IntermediateThrowEvent') ||
      is$4(parent, 'bpmn:EndEvent')
    ) {
      // message throw events are service task likes and need a type and not a message
      return
    }

    if (!node.messageRef) {
      reporter.report(node.$parent.id, 'Property “Global Message“ is missing.')
    }
    if (node.messageRef && !is$4(node.$parent, 'bpmn:StartEvent')) {
      if (
        extensionElementsHelper(node.messageRef, 'zeebe:subscription').length ==
          0 &&
        extensionElementsHelper(node.messageRef, 'zeebe:Subscription').length ==
          0
      ) {
        reporter.report(
          node.$parent.id,
          'Property “Correlation Key“ is missing.',
        )
      }
    }
  }

  return {
    check: check,
  }
}

const { is: is$5 } = index_esm

var noProcessid = function () {
  function check(node, reporter) {
    if (!is$5(node, 'bpmn:CallActivity')) {
      return
    }

    const calledElement =
      extensionElementsHelper(node, 'zeebe:calledElement')[0] ||
      extensionElementsHelper(node, 'zeebe:CalledElement')[0]
    if (!calledElement || !calledElement.processId) {
      reporter.report(node.id, 'Property “process ID” is missing.')
    }
  }

  return {
    check: check,
  }
}

const { is: is$6 } = index_esm

var noTimerdefinition = function () {
  function check(node, reporter) {
    if (!is$6(node, 'bpmn:TimerEventDefinition')) {
      return
    }

    let timeCycle = node.timeCycle
    if (timeCycle) {
      timeCycle = timeCycle.body
    }
    let timeDuration = node.timeDuration
    if (timeDuration) {
      timeDuration = timeDuration.body
    }
    let timeDate = node.timeDate
    if (timeDate) {
      timeDate = timeDate.body
    }

    // catch event can be duration only right now
    if (is$6(node.$parent, 'bpmn:IntermediateCatchEvent')) {
      if (!timeDuration) {
        reporter.report(
          node.$parent.id,
          'Property “Timer Duration” is missing.',
        )
      }
    }
    // start can be cycle or date
    if (is$6(node.$parent, 'bpmn:StartEvent')) {
      if (!timeCycle && !timeDate) {
        reporter.report(
          node.$parent.id,
          'Property “Timer Cycle or Date” is missing.',
        )
      }
    }
    // boundary event can be interrupting or not -> Cycle or Duration
    if (is$6(node.$parent, 'bpmn:BoundaryEvent')) {
      if (!timeDuration && !timeCycle) {
        reporter.report(
          node.$parent.id,
          'Property “Timer Duration or Cycle” is missing.',
        )
      }
    }
  }

  return {
    check: check,
  }
}

const { is: is$7 } = index_esm

/**
 * A rule that checks whether not more than one blank start event
 * exists per scope.
 */
var singleBlankStartEvent = function () {
  function check(node, reporter) {
    if (!is$7(node, 'bpmn:FlowElementsContainer')) {
      return
    }

    const flowElements = node.flowElements || []

    const blankStartEvents = flowElements.filter(function (flowElement) {
      if (!is$7(flowElement, 'bpmn:StartEvent')) {
        return false
      }

      const eventDefinitions = flowElement.eventDefinitions || []

      return eventDefinitions.length === 0
    })

    if (blankStartEvents.length > 1) {
      const type = is$7(node, 'bpmn:SubProcess') ? 'Sub process' : 'Process'

      reporter.report(node.id, type + ' has multiple blank start events')
    }
  }

  return {
    check,
  }
}

const { is: is$8, isAny: isAny$2 } = index_esm

/**
 * A rule that checks for the presence of a start event per scope.
 */
var startEventRequired = function () {
  function hasStartEvent(node) {
    const flowElements = node.flowElements || []

    return flowElements.some((node) => is$8(node, 'bpmn:StartEvent'))
  }

  function check(node, reporter) {
    if (!isAny$2(node, ['bpmn:Process', 'bpmn:SubProcess'])) {
      return
    }

    if (!hasStartEvent(node)) {
      const type = is$8(node, 'bpmn:SubProcess') ? 'Sub process' : 'Process'

      reporter.report(node.id, type + ' is missing start event')
    }
  }

  return { check }
}

const { is: is$9, isAny: isAny$3 } = index_esm

/**
 * A rule that checks the presence of an end event per scope.
 */
var endEventRequired = function () {
  function hasEndEvent(node) {
    const flowElements = node.flowElements || []

    return flowElements.some((node) => is$9(node, 'bpmn:EndEvent'))
  }

  function check(node, reporter) {
    if (!isAny$3(node, ['bpmn:Process', 'bpmn:SubProcess'])) {
      return
    }

    if (!hasEndEvent(node)) {
      const type = is$9(node, 'bpmn:SubProcess') ? 'Sub process' : 'Process'

      reporter.report(node.id, type + ' is missing end event')
    }
  }

  return { check }
}

const { is: is$a } = index_esm

/**
 * A rule that checks, whether a gateway has only one source and target.
 *
 * Those gateways are superfluous since they don't do anything.
 */
var superfluousGateway = function () {
  function check(node, reporter) {
    if (!is$a(node, 'bpmn:Gateway')) {
      return
    }

    const incoming = node.incoming || []
    const outgoing = node.outgoing || []

    if (incoming.length === 1 && outgoing.length === 1) {
      reporter.report(
        node.id,
        'Gateway is superfluous. It only has one source and target.',
      )
    }
  }

  return {
    check,
  }
}

const cache = {}

/**
 * A resolver that caches rules and configuration as part of the bundle,
 * making them accessible in the browser.
 *
 * @param {Object} cache
 */
function Resolver() {}

Resolver.prototype.resolveRule = function (pkg, ruleName) {
  const rule = cache[pkg + '/' + ruleName]

  if (!rule) {
    throw new Error('cannot resolve rule <' + pkg + '/' + ruleName + '>')
  }

  return rule
}

Resolver.prototype.resolveConfig = function (pkg, configName) {
  throw new Error('cannot resolve config <' + configName + '> in <' + pkg + '>')
}

const resolver = new Resolver()

const rules = {
  'camunda-cloud/no-undefined-task': 'error',
  'camunda-cloud/no-type': 'error',
  'camunda-cloud/conditional-sequence-flows': 'error',
  'camunda-cloud/no-errordefinition': 'error',
  'camunda-cloud/no-messagedefinition': 'error',
  'camunda-cloud/no-processid': 'error',
  'camunda-cloud/no-timerdefinition': 'error',
  'single-blank-start-event': 'error',
  'start-event-required': 'error',
  'end-event-required': 'error',
  'superfluous-gateway': 'error',
}

const config = {
  rules: rules,
}

const bundle = {
  resolver: resolver,
  config: config,
}
cache['bpmnlint-plugin-camunda-cloud/no-undefined-task'] = noUndefinedTask
cache['bpmnlint-plugin-camunda-cloud/no-type'] = noType
cache[
  'bpmnlint-plugin-camunda-cloud/conditional-sequence-flows'
] = conditionalSequenceFlows
cache['bpmnlint-plugin-camunda-cloud/no-errordefinition'] = noErrordefinition
cache[
  'bpmnlint-plugin-camunda-cloud/no-messagedefinition'
] = noMessagedefinition
cache['bpmnlint-plugin-camunda-cloud/no-processid'] = noProcessid
cache['bpmnlint-plugin-camunda-cloud/no-timerdefinition'] = noTimerdefinition
cache['bpmnlint/single-blank-start-event'] = singleBlankStartEvent
cache['bpmnlint/start-event-required'] = startEventRequired
cache['bpmnlint/end-event-required'] = endEventRequired
cache['bpmnlint/superfluous-gateway'] = superfluousGateway

exports.config = config
exports.default = bundle
exports.resolver = resolver
